import React, { useState } from 'react'
import dayjs from 'dayjs'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import {
  LoadAccountMemberList, LoadFormList, LoadProjectList, LoadResourcePlaceList, LoadWorkFinishOptionList
} from '@/javascript/domain/usecases'
import { AutocompleteFetch, ResourcePlacesInput, SearchInput } from '@/javascript/presentation/components'
import { DateInput, ViewModeInput, WorkStatusInput, WorkFinishOptionsInput } from './components'
import fetchDataToInput from './helpers/data-to-input'
import { FetchLocationsParams } from '../../work-user-maps'
import { FilterButton } from '@/javascript/presentation/components/buttons'

type Props = {
  fetchLocations: (params?: FetchLocationsParams) => Promise<void>
  fetchAccountMembers: LoadAccountMemberList
  fetchForms: LoadFormList
  fetchProjects: LoadProjectList
  fetchResourcePlaces: LoadResourcePlaceList
  fetchWorkFinishOptions: LoadWorkFinishOptionList
  isReady: boolean
}

type ResourcePlace = {
  value: string
  label: string
  hierarchyLatitude?: number
  hierarchyLongitude?: number
}

const Filters: React.FC<Props> = (props) => {
  const viewOptions = [{ label: "Atividades", value: "works" }, { label: "Equipe", value: "users" }]
  const workStatusOptions =[
    { label: "A realizar", value: "not_started" }, { label: "Em andamento", value: "started" },
    { label: "Finalizado", value: "finished" }, { label: "Revisado", value: "reviewed" }
  ]
  const [viewMode, setViewMode] = useState([viewOptions[0], viewOptions[1]])
  const [date, setDate] = useState(dayjs())
  const [forms, setForms] = useState([])
  const [resourcePlaces, setResourcePlaces] = useState<ResourcePlace[]>([])
  const [projects, setProjects] = useState([])
  const [inspectors, setInspectors] = useState([])
  const [providers, setProviders] = useState([])
  const [workStatus, setWorkStatus] = useState('')
  const [finishOptions, setFinishOptions] = useState([])
  const [searchText, setSearchText] = useState('')
  const handleRequestFilters = (date: dayjs.Dayjs | null, viewMode: { label: string, value: string }[]) => {
    if (date) {
      props.fetchLocations({
        date: date.toDate(),
        viewMode: viewMode.map(option => option.value),
        searchText,
        resourcePlaces: resourcePlaces.map(option => option.value),
        forms: forms.map(option => option.value),
        projects: projects.map(option => option.value),
        providers: providers.map(option => option.value),
        inspectors: inspectors.map(option => option.value),
        finishOptions: finishOptions.map(option => option.value),
        status: workStatus
      })
    }
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '16px' }} data-testid="filters">
      <Typography variant="h7" color={'text.secondary'}>Filtros</Typography>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', alignContent: 'flex-start', gap: '16px 24px', flexWrap: 'wrap' }}>
        <ViewModeInput viewMode={viewMode} setViewMode={setViewMode} viewOptions={viewOptions} />
        <DateInput date={date} setDate={setDate} />
        <ResourcePlacesInput
          resourcePlaces={resourcePlaces} setResourcePlaces={setResourcePlaces}
          fetchResourcePlaces={(searchParam) => fetchDataToInput(props.fetchResourcePlaces, searchParam)}
          optionsProps={{
            tooltipTitle: 'Não possui endereço cadastrado',
            disable: (option: ResourcePlace) => !option.hierarchyLatitude || !option.hierarchyLongitude
          }}
        />
        <AutocompleteFetch
          label="Formulário/Checklist" value={forms} setValue={setForms}
          fetchOptions={(searchParam) => fetchDataToInput(props.fetchForms, searchParam)}
        />
        <AutocompleteFetch
          label="Projetos" value={projects} setValue={setProjects}
          fetchOptions={(searchParam) => fetchDataToInput(props.fetchProjects, searchParam)}
        />
        <WorkStatusInput workStatus={workStatus} setWorkStatus={setWorkStatus} workStatusOptions={workStatusOptions} />
        <WorkFinishOptionsInput
          workFinishOptions={finishOptions} setWorkFinishOptions={setFinishOptions}
          fetchWorkFinishOptions={(searchParam) => fetchDataToInput(props.fetchWorkFinishOptions, searchParam)}
          workStatus={workStatus}
        />
        <AutocompleteFetch
          label="Responsáveis - Acompanhamento" value={inspectors} setValue={setInspectors}
          fetchOptions={(searchParam) => fetchDataToInput(props.fetchAccountMembers, searchParam)}
        />
        <AutocompleteFetch
          label="Responsáveis - Execução" value={providers} setValue={setProviders}
          fetchOptions={(searchParam) => fetchDataToInput(props.fetchAccountMembers, searchParam)}
        />
        <SearchInput value={searchText} setValue={setSearchText} />
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '24px' }}>
        <FilterButton
          variant="apply"
          onClick={() => handleRequestFilters(date, viewMode)}
          disabled={
            !props.isReady || (date === null || date.isValid() === false || viewMode.length === 0)
          }
          tooltipProps={{
            title: 'Altere os filtros para aplicar alterações na tela',
            disableHover: !(date === null || date.isValid() === false || viewMode.length === 0)
          }}
        />
        <FilterButton
          variant="clear"
          disabled={
            !props.isReady || (dayjs(date).isSame(dayjs(), 'date') && viewMode.length === 2 && finishOptions.length === 0 &&
            resourcePlaces.length === 0 && forms.length === 0 && projects.length === 0
            && inspectors.length === 0 && providers.length === 0 && workStatus === '' && searchText === '')
          }
          onClick={() => {
            setDate(dayjs())
            setViewMode([viewOptions[0], viewOptions[1]])
            setResourcePlaces([])
            setForms([])
            setProjects([])
            setInspectors([])
            setProviders([])
            setFinishOptions([])
            setWorkStatus('')
            setSearchText('')
          }}
        />
      </Box>
    </Box>
  )
}

export default Filters
